// IFF (Identification Friend or Foe) + plattform-profilbibliotek. To ting:
//  1) Tolke IFF-transponder-svar (Mode 1/2/3-A/4/5/C/S) til en venn/ukjent/
//     fiende-DISPOSISJON med begrunnelse — beslutningsstøtte for ID, ALDRI en
//     autonom skyte-avgjørelse (menneske i loopen, jf. RoE/fireapproval).
//  2) Et bibliotek av plattform-profiler (fly/kjøretøy/fartøy) som rangerer
//     sannsynlig type ut fra kinematikk (fart/høyde/lengde) — hjelp til å tolke
//     et umerket spor. Rent + deterministisk + testbart.
import { z } from 'zod';
// ── IFF-transponder ────────────────────────────────────────────────────────
/** Nød-squawks (Mode 3/A) med fast betydning (ICAO). */
export const EMERGENCY_SQUAWK = {
    '7500': 'Kapring (unlawful interference)',
    '7600': 'Sambandssvikt (radio failure)',
    '7700': 'Generell nød (emergency)',
};
/** Er dette et gyldig Mode 3/A-squawk? (fire oktale sifre, 0–7). */
export function isValidSquawk(s) {
    return /^[0-7]{4}$/.test(s);
}
export const IffReplySchema = z.object({
    /** Mode 3/A oktal-squawk (fire sifre) — sivil/militær, IKKE kryptosikret. */
    mode3a: z.string().optional(),
    /** Mode C — trykkhøyde i fot (rapportert av transponderen). */
    modeCAltFt: z.number().optional(),
    /** Mode 4 — kryptosikret venn-svar (kald krig-arv). */
    mode4: z.enum(['valid', 'invalid', 'no-response']).optional(),
    /** Mode 5 — moderne kryptosikret (spread-spektrum). */
    mode5: z.enum(['valid', 'invalid', 'no-response']).optional(),
    /** Mode S — 24-bit ICAO-adresse (hex) hvis kjent. */
    modeSAddress: z.string().optional(),
    /** Ble en sikker forespørsel (Mode 4/5) i det hele tatt sendt/forventet? */
    interrogatedSecure: z.boolean().default(false),
});
export const IFF_DISPOSITION_LABEL = {
    friend: 'Venn',
    'assumed-friend': 'Antatt venn',
    unknown: 'Ukjent',
    suspect: 'Mistenkt',
    hostile: 'Fiende',
};
/** APP-6 standard-identitet som disposisjonen kartlegges til (symbol-ramme). */
export const IFF_APP6_IDENTITY = {
    friend: 'friend',
    'assumed-friend': 'assumed-friend',
    unknown: 'unknown',
    suspect: 'suspect',
    hostile: 'hostile',
};
/**
 * Vurder en IFF-respons → disposisjon. Kryptosikret svar (Mode 4/5 «valid») er
 * det eneste som gir «friend». Manglende/ugyldig sikkert svar HEVER, aldri
 * senker, mistanke. Fravær av IFF er IKKE bevis på fiendtlighet (mange venner
 * har defekt/avslått transponder) → «unknown/suspect», aldri «hostile» alene.
 * Fiende krever positiv indikasjon (ugyldig sikkert svar ved forespørsel).
 */
export function assessIff(reply) {
    const reasons = [];
    const r = IffReplySchema.parse(reply);
    const emergency = r.mode3a ? EMERGENCY_SQUAWK[r.mode3a] : undefined;
    if (emergency)
        reasons.push(`Nød-squawk ${r.mode3a}: ${emergency}`);
    // Sikkert svar er sterkeste signal.
    const secure = r.mode5 ?? r.mode4;
    const secureLabel = r.mode5 ? 'Mode 5' : 'Mode 4';
    if (secure === 'valid') {
        reasons.push(`${secureLabel} gyldig kryptosvar → venn`);
        return { disposition: 'friend', reasons, emergency, requiresVisualId: false };
    }
    let disposition = 'unknown';
    if (secure === 'invalid') {
        reasons.push(`${secureLabel} UGYLDIG svar på sikker forespørsel → mistenkt`);
        disposition = 'suspect';
    }
    else if (r.interrogatedSecure && secure === 'no-response') {
        reasons.push(`Ingen svar på sikker ${secureLabel}-forespørsel → mistenkt`);
        disposition = 'suspect';
    }
    else if (r.mode3a && isValidSquawk(r.mode3a)) {
        reasons.push(`Mode 3/A ${r.mode3a} (ikke-sikret) → antatt venn ved gyldig oppdragskode`);
        disposition = 'assumed-friend';
    }
    else {
        reasons.push('Ingen IFF — kan ikke identifiseres (fravær ≠ fiendtlig)');
        disposition = 'unknown';
    }
    // Nød-squawk demper mistanke (venn i knipe) men fjerner den ikke.
    if (emergency && disposition === 'suspect') {
        reasons.push('Nød-squawk tilsier mulig venn i nød — krev visuell ID');
    }
    return { disposition, reasons, emergency, requiresVisualId: true };
}
/** Representativt, åpent-kilde-basert bibliotek (ikke uttømmende, ugradert). */
export const PLATFORM_LIBRARY = [
    // Luft
    { id: 'fighter', name: 'Jagerfly', domain: 'air', app6Dimension: 'air', speedKt: [300, 1200], altitudeFt: [1000, 55000], lengthM: [15, 22], note: 'Høy fart, høy manøver' },
    { id: 'helo', name: 'Helikopter', domain: 'air', app6Dimension: 'air', speedKt: [0, 170], altitudeFt: [0, 12000], lengthM: [12, 22], note: 'Kan henge/lav fart' },
    { id: 'uas-small', name: 'Liten drone (UAS)', domain: 'air', app6Dimension: 'air', speedKt: [0, 90], altitudeFt: [0, 15000], lengthM: [0.3, 4], note: 'Lav fart, lite tverrsnitt' },
    { id: 'uas-medium', name: 'MALE-drone', domain: 'air', app6Dimension: 'air', speedKt: [70, 250], altitudeFt: [5000, 45000], lengthM: [8, 20], note: 'Loitering ISR/strike' },
    { id: 'transport', name: 'Transportfly', domain: 'air', app6Dimension: 'air', speedKt: [200, 500], altitudeFt: [1000, 40000], lengthM: [30, 75], note: 'Stort, jevn kurs' },
    { id: 'cruise-missile', name: 'Kryssermissil', domain: 'air', app6Dimension: 'air', speedKt: [400, 600], altitudeFt: [50, 1000], lengthM: [4, 9], note: 'Lav høyde, høy fart' },
    // Land
    { id: 'mbt', name: 'Stridsvogn (MBT)', domain: 'land', app6Dimension: 'land-unit', speedKt: [0, 40], lengthM: [7, 11], note: 'Tung beltevogn' },
    { id: 'apc', name: 'Pansret vogn (APC/IFV)', domain: 'land', app6Dimension: 'land-unit', speedKt: [0, 60], lengthM: [5, 8], note: 'Belter/hjul' },
    { id: 'truck', name: 'Lastebil/kjøretøy', domain: 'land', app6Dimension: 'land-unit', speedKt: [0, 70], lengthM: [4, 12], note: 'Logistikk/transport' },
    { id: 'dismount', name: 'Avsittet soldat', domain: 'land', app6Dimension: 'land-unit', speedKt: [0, 8], lengthM: [0.5, 2], note: 'Til fots' },
    // Sjø
    { id: 'frigate', name: 'Fregatt/krigsskip', domain: 'sea', app6Dimension: 'sea-surface', speedKt: [0, 32], lengthM: [100, 150], note: 'Stor overflatestridsenhet' },
    { id: 'fac', name: 'Hurtig angreps­fartøy (FAC)', domain: 'sea', app6Dimension: 'sea-surface', speedKt: [15, 45], lengthM: [20, 60], note: 'Liten, rask' },
    { id: 'merchant', name: 'Handelsfartøy', domain: 'sea', app6Dimension: 'sea-surface', speedKt: [8, 25], lengthM: [90, 400], note: 'Stort, jevn kurs' },
    { id: 'smallboat', name: 'Småbåt/RHIB', domain: 'sea', app6Dimension: 'sea-surface', speedKt: [0, 50], lengthM: [3, 15], note: 'Lite tverrsnitt' },
    // Undervanns
    { id: 'submarine', name: 'Ubåt', domain: 'subsurface', app6Dimension: 'subsurface', speedKt: [2, 30], altitudeFt: [-1500, 0], lengthM: [50, 170], note: 'Neddykket' },
];
/** Poeng for hvor godt en verdi ligger i et intervall (1 = innenfor, faller lineært
 * mot 0 med avstand utenfor, skalert til intervallbredden). */
function bandScore(v, [lo, hi]) {
    if (v >= lo && v <= hi)
        return 1;
    const width = Math.max(1, hi - lo);
    const dist = v < lo ? lo - v : v - hi;
    return Math.max(0, 1 - dist / width);
}
/**
 * Ranger plattform-profiler etter hvor godt kinematikken passer. Domene (hvis
 * gitt) filtrerer først. Snittet av tilgjengelige dimensjons-poeng (fart/høyde/
 * lengde) er scoren. Returnerer sortert (best først), kun score > 0.
 */
export function classifyByKinematics(hint, lib = PLATFORM_LIBRARY) {
    const pool = hint.domain ? lib.filter((p) => p.domain === hint.domain) : lib;
    const out = [];
    for (const p of pool) {
        const parts = [];
        if (hint.speedKt !== undefined)
            parts.push(bandScore(hint.speedKt, p.speedKt));
        if (hint.altitudeFt !== undefined && p.altitudeFt)
            parts.push(bandScore(hint.altitudeFt, p.altitudeFt));
        if (hint.lengthM !== undefined && p.lengthM)
            parts.push(bandScore(hint.lengthM, p.lengthM));
        if (!parts.length)
            continue; // ingen sammenlignbare dimensjoner
        const score = parts.reduce((a, b) => a + b, 0) / parts.length;
        if (score > 0)
            out.push({ profile: p, score });
    }
    return out.sort((a, b) => b.score - a.score || a.profile.id.localeCompare(b.profile.id));
}
/** Profil-oppslag på id. */
export function platformById(id) {
    return PLATFORM_LIBRARY.find((p) => p.id === id);
}
//# sourceMappingURL=iff.js.map